<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import AppointmentBookingModal from 'dashboard/components-next/appointments/AppointmentBookingModal.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const showBookingModal = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat);

// Agendar aparece só para quem usa agendamentos — e falha ABERTO de propósito.
// O getter `isFeatureEnabledonAccount` devolve false tanto para "a conta
// desligou agendamentos" quanto para "a lista de features ainda não chegou", e
// as duas coisas não significam o mesmo: sumir com o botão porque a licença
// não sincronizou é o modo de falha que motivou a remoção deste gate em
// a126e01c34. Por isso a decisão é tomada sobre o mapa de features direto —
// esconde apenas quando a conta TEM features carregadas e a de agendamentos
// está apagada entre elas. Todo plano concede appointments_module
// (planFeatureList ignora o nome do plano), então isto é um interruptor por
// conta, nunca uma restrição por plano.
const isAppointmentsEnabled = computed(() => {
  const getAccount = store.getters['accounts/getAccount'];
  const features =
    typeof getAccount === 'function'
      ? getAccount(store.getters.getCurrentAccountId)?.features
      : null;
  if (!features || !Object.keys(features).length) return true;
  return Boolean(features[FEATURE_FLAGS.APPOINTMENTS_MODULE]);
});

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <!-- Agendar sai do menu ⋮ e vira ação visível ao lado do status: marcar
         durante o atendimento é rotina, e um item escondido atrás de dois
         cliques não é encontrado por quem ainda não sabe que existe. Fica só
         aqui — dois caminhos para a mesma ação é o que a barra de abas do
         módulo já custou uma vez. -->
    <ButtonV4
      v-if="isAppointmentsEnabled"
      v-tooltip="t('APPOINTMENTS.MENU.BOOK_APPOINTMENT')"
      size="sm"
      variant="ghost"
      color="slate"
      icon="i-lucide-calendar-plus"
      class="rounded-md hover:bg-n-alpha-2"
      data-testid="book-appointment-action"
      :aria-label="t('APPOINTMENTS.MENU.BOOK_APPOINTMENT')"
      @click="showBookingModal = true"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <AppointmentBookingModal
      v-if="showBookingModal"
      :show="showBookingModal"
      :conversation-id="currentChat.id"
      @close="showBookingModal = false"
      @created="() => useAlert(t('APPOINTMENTS.BOOKING_MODAL.SUCCESS'))"
    />
  </div>
</template>
